import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import {
  ModalBodyContent,
  ModalButtonsContainer,
  ModalHeader,
  SectionContainer,
  StyledModalBody,
  StyledModalV2
} from '../../components/Modals';
import { Flex } from '../../components/ui/Flex';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../components/ui/Form';
import { ButtonBodyVariant } from '../../components/ui/ButtonVariant';
import { Select, SelectItem } from '../../components/ui/Select';
import ToggleInput from '../../components/ui/ToggleInput';
import ConfirmModal from '../../components/ConfirmModal';
import { changeOpenedModal } from '../../redux/actions/headerModal';
import { useScrollToBottom } from '../../hooks/useScrollToBottom';
import {
  canChooseDocOrgScope,
  canSetFolderShared,
  RESOURCE_CENTER_FILE_TYPE
} from './constants';
import {
  createFolder,
  getDocuments,
  getFolderDocuments,
  updateFolder
} from '../../redux/actions/documents';
import { setToast } from '../../redux/actions/window';
import { requestPending } from '../../utils/status';
import { CREATE_FOLDER, UPDATE_FOLDER } from '../../redux/constants';
import { UserRole } from '../users/constant';
import { DocumentNameInput } from './documentWizardStyles';

const hasDocumentStorageEnabled = (building) => !!building?.config?.enable_document_storage;

const AddEditFolderModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const modalBodyRef = useRef(null);
  const { isAtBottom } = useScrollToBottom(modalBodyRef);
  const modal = useSelector((state) => state.headerModal.modal);
  const modalData = useSelector((state) => state.headerModal.data);
  const globalOrgId = useSelector((state) => state.Organization.globalOrgId);
  const organization = useSelector((state) => state.Organization.organization);
  const globalBuildingId = useSelector((state) => state.building.globalBuildingId);
  const allBuildings = useSelector((state) => state.building.allBuildings);
  const roleId = useSelector((state) => state.auth.roleId);
  const {
    createFolderStatus,
    updateFolderStatus,
    lastListParams,
    lastListMode,
    lastFolderId
  } = useSelector((state) => state.documents);

  const [pendingUnshareValues, setPendingUnshareValues] = useState(null);

  const isOpen = modal === 'addEditFolder';
  const isEdit = isOpen && Boolean(modalData?.id);
  const isSubmitting =
    createFolderStatus === requestPending(CREATE_FOLDER) ||
    updateFolderStatus === requestPending(UPDATE_FOLDER);

  const orgId = globalOrgId || organization?.id;
  const showSharedToggle = canSetFolderShared(roleId);

  const documentStorageBuildings = useMemo(
    () => (allBuildings || []).filter(hasDocumentStorageEnabled),
    [allBuildings]
  );

  const showBuildingSelect = canChooseDocOrgScope(roleId, documentStorageBuildings.length || 0);

  const defaultBuildingId = useMemo(() => {
    if (!(globalBuildingId > 0)) {
      return null;
    }
    const globalHasStorage = documentStorageBuildings.some(
      (building) => Number(building.id) === Number(globalBuildingId)
    );
    return globalHasStorage ? globalBuildingId : null;
  }, [documentStorageBuildings, globalBuildingId]);

  const schema = Yup.object().shape({
    name: Yup.string()
      .trim()
      .required(t('Name is required', { defaultValue: 'Name is required' }))
      .max(255, t('Name is too long', { defaultValue: 'Name is too long' })),
    buildingId: Yup.mixed().test('building-required', t('Building is required'), function (value) {
      if (this.options.context?.isEdit) {
        return true;
      }
      return Boolean(value) && Number(value) > 0;
    }),
    shared: Yup.boolean()
  });

  const form = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
    context: { isEdit },
    defaultValues: {
      name: '',
      buildingId: defaultBuildingId,
      shared: false
    }
  });

  useEffect(() => {
    if (!isOpen) {
      setPendingUnshareValues(null);
      return;
    }

    if (isEdit && modalData) {
      form.reset({
        name: modalData.name || '',
        buildingId: modalData.building_id || defaultBuildingId,
        shared: Boolean(modalData.shared)
      });
      return;
    }

    form.reset({
      name: '',
      buildingId: defaultBuildingId,
      shared: false
    });
  }, [isOpen, isEdit, modalData, form, defaultBuildingId]);

  const closeModal = () => {
    dispatch(changeOpenedModal({ modal: '' }));
  };

  const refreshList = () => {
    if (lastListMode === 'folder' && lastFolderId) {
      dispatch(
        getFolderDocuments({
          id: lastFolderId,
          params: { ...(lastListParams || {}) }
        })
      );
      return;
    }

    if (lastListParams) {
      dispatch(getDocuments({ params: { ...lastListParams } }));
      return;
    }

    if (!orgId || !(globalBuildingId > 0)) {
      return;
    }

    const params = {
      type: RESOURCE_CENTER_FILE_TYPE,
      page: 1,
      building: globalBuildingId
    };

    if (roleId === UserRole.SUPER_ADMIN) {
      params.org = orgId;
    }

    dispatch(getDocuments({ params }));
  };

  const submitFolder = (values) => {
    const name = values.name?.trim() || '';
    const shared = showSharedToggle ? Boolean(values.shared) : false;

    if (isEdit) {
      const body = { name };
      if (showSharedToggle) {
        body.shared = shared;
      }

      const params = {};
      if (roleId === UserRole.SUPER_ADMIN && orgId) {
        params.org = orgId;
      }

      dispatch(
        updateFolder({
          id: modalData.id,
          body,
          params,
          success: () => {
            dispatch(
              setToast({
                toastShow: true,
                toastMessage: t('Folder updated successfully', {
                  defaultValue: 'Folder updated successfully'
                })
              })
            );
            refreshList();
            closeModal();
          },
          fail: () => {
            dispatch(
              setToast({
                toastShow: true,
                toastMessage: t('Failed to update folder', {
                  defaultValue: 'Failed to update folder'
                }),
                modal: 'errorToast'
              })
            );
          }
        })
      );
      return;
    }

    const buildingId = showBuildingSelect
      ? Number(values.buildingId)
      : Number(globalBuildingId);

    if (!(buildingId > 0)) {
      return;
    }

    const params = {};
    if (roleId === UserRole.SUPER_ADMIN && orgId) {
      params.org = orgId;
    }

    dispatch(
      createFolder({
        body: {
          name,
          building_id: buildingId,
          shared
        },
        params,
        success: () => {
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: t('Folder created successfully', {
                defaultValue: 'Folder created successfully'
              })
            })
          );
          refreshList();
          closeModal();
        },
        fail: () => {
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: t('Failed to create folder', {
                defaultValue: 'Failed to create folder'
              }),
              modal: 'errorToast'
            })
          );
        }
      })
    );
  };

  // Turning a shared folder local kicks out globally shared files and files from other
  // buildings, so confirm before hitting the API.
  const onSubmit = (values) => {
    const isUnsharing =
      isEdit && showSharedToggle && Boolean(modalData?.shared) && !values.shared;

    if (isUnsharing) {
      setPendingUnshareValues(values);
      return;
    }

    submitFolder(values);
  };

  const confirmUnshare = () => {
    const values = pendingUnshareValues;
    setPendingUnshareValues(null);
    if (values) {
      submitFolder(values);
    }
  };

  if (!isOpen) {
    return null;
  }

  const buildingName =
    documentStorageBuildings.find(
      (building) => Number(building.id) === Number(form.watch('buildingId') || defaultBuildingId)
    )?.name || '';

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <StyledModalV2 show={isOpen} side width="550px">
          <StyledModalBody innerRef={(el) => (modalBodyRef.current = el)}>
            <ModalHeader
              title={
                isEdit
                  ? t('Edit Folder', { defaultValue: 'Edit Folder' })
                  : t('Create Folder', { defaultValue: 'Create Folder' })
              }
              v2
            />
            <ModalBodyContent>
              <SectionContainer>
                <Flex direction="column" gap="16px">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem alt>
                        <FormLabel>{t('Name', { defaultValue: 'Name' })}</FormLabel>
                        <FormControl>
                          <DocumentNameInput
                            {...field}
                            placeholder={t('Folder name', {
                              defaultValue: 'Folder name'
                            })}
                            maxLength={255}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {!isEdit && showBuildingSelect ? (
                    <FormField
                      control={form.control}
                      name="buildingId"
                      render={({ field }) => (
                        <FormItem alt>
                          <FormLabel>{t('Building', { defaultValue: 'Building' })}</FormLabel>
                          <FormControl>
                            <Select
                              id="folder_building_id"
                              placeholder={t('Select building', {
                                defaultValue: 'Select building'
                              })}
                              value={field.value || undefined}
                              onChange={field.onChange}
                              onBlur={field.onBlur}
                              height="48px">
                              {documentStorageBuildings.length > 0 ? (
                                documentStorageBuildings.map((building) => (
                                  <SelectItem key={building.id} value={building.id}>
                                    {building.name}
                                  </SelectItem>
                                ))
                              ) : (
                                <SelectItem value={-1} disabled>
                                  {t('No Buildings Available')}
                                </SelectItem>
                              )}
                            </Select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  ) : null}

                  {!isEdit && !showBuildingSelect && buildingName ? (
                    <Flex direction="column" gap="4px">
                      <FormLabel>{t('Building', { defaultValue: 'Building' })}</FormLabel>
                      <span>{buildingName}</span>
                    </Flex>
                  ) : null}

                  {showSharedToggle ? (
                    <ToggleInput
                      form={form}
                      name="shared"
                      label={t('Shared', { defaultValue: 'Shared' })}
                      description={t(
                        'Shared folders are visible across buildings and can hold org-wide files or files from other buildings.',
                        {
                          defaultValue:
                            'Shared folders are visible across buildings and can hold org-wide files or files from other buildings.'
                        }
                      )}
                      disabled={isSubmitting}
                    />
                  ) : null}
                </Flex>
              </SectionContainer>
            </ModalBodyContent>
          </StyledModalBody>

          <ModalButtonsContainer gapY="8px" justify="flex-end" isAtBottom={isAtBottom}>
            <ButtonBodyVariant
              type="submit"
              disabled={
                isSubmitting || !form.formState.isValid || Boolean(pendingUnshareValues)
              }>
              {isEdit
                ? t('Save changes', { defaultValue: 'Save changes' })
                : t('Create folder', { defaultValue: 'Create folder' })}
            </ButtonBodyVariant>
            <ButtonBodyVariant
              variant="secondary"
              type="button"
              onClick={closeModal}
              disabled={isSubmitting || Boolean(pendingUnshareValues)}>
              {t('Cancel')}
            </ButtonBodyVariant>
          </ModalButtonsContainer>
        </StyledModalV2>

        <ConfirmModal
          show={Boolean(pendingUnshareValues)}
          title={t('Restrict this folder to this building?', { defaultValue: 'Restrict this folder to this building' })}
          description={t(
            'Changing this folder to be specific to this building will move any globally shared files or files belonging to other buildings outside of this folder.',
            {
              defaultValue:
                'Changing this folder to be specific to this building will move any globally shared files or files belonging to other buildings outside of this folder.'
            }
          )}
          confirmText={t('Confirm', { defaultValue: 'Confirm' })}
          cancelText={t('Cancel')}
          onConfirm={confirmUnshare}
          onCancel={() => setPendingUnshareValues(null)}
          disabled={isSubmitting}
        />
      </form>
    </Form>
  );
};

export default AddEditFolderModal;
